<template>
    <div :class="containerClasses">
        <b-row align-v="end" align-h="center">
            <b-col v-if="titles.save" cols="3">
                <n-button type="success" :disabled="isSaveDisabled" block @click="handleSave"
                    v-b-tooltip.hover.ds750 :title="tooltips.save">
                    {{ titles.save }}
                </n-button>
            </b-col>

            <b-col v-if="titles.saveAndStay" cols="3">
                <n-button type="primary" :disabled="isSaveDisabled" block @click="handleSaveAndStay"
                    v-b-tooltip.hover.ds750 :title="tooltips.saveAndStay">
                    {{ titles.saveAndStay }}
                </n-button>
            </b-col>

            <b-col v-if="titles.new" cols="3">
                <n-button type="info" :disabled="isNewDisabled" block @click="handleNew"
                    v-b-tooltip.hover.ds750 :title="tooltips.new">
                    {{ titles.new }}
                </n-button>
            </b-col>

            <b-col v-if="titles.clear" cols="3">
                <n-button type="danger" :disabled="isClearDisabled" block @click="handleClear"
                    v-b-tooltip.hover.ds750 :title="tooltips.clear">
                    {{ titles.clear }}
                </n-button>
            </b-col>

            <b-col v-if="titles.cancel" cols="3">
                <n-button type="dark" :disabled="disableCancel" block @click="handleCancel"
                    v-b-tooltip.hover.ds750 :title="tooltips.cancel">
                    {{ titles.cancel }}
                </n-button>
            </b-col>
        </b-row>
    </div>
</template>

<script lang="ts">
import { Component, Emit, Prop, Vue, Watch } from 'vue-property-decorator';
import { ButtonTitles, DEFAULT_TITLES } from "@/components/ButtonTitles";

const DEFAULT_CLEAR_MESSAGE = 'Are you sure you want to clear?';

@Component({
    name: 'NewClearSaveButtons',
})
export default class NewClearSaveButtons extends Vue {
    @Prop({ default: () => DEFAULT_TITLES })
    readonly titles!: ButtonTitles;

    @Prop({ default: () => ({}) })
    readonly tooltips!: ButtonTitles;

    @Prop({ type: Boolean, default: false })
    readonly sticky!: boolean;

    @Prop({ type: Boolean, default: null })
    readonly disable!: boolean;

    @Prop({ type: Boolean, default: false })
    readonly disableNew!: boolean;

    @Prop({ type: Boolean, default: true })
    readonly disableClear!: boolean;

    @Prop({ type: Boolean, default: true })
    readonly disableSave!: boolean;

    @Prop({ type: Boolean, default: false })
    readonly disableCancel!: boolean;

    @Prop({ type: Boolean, default: false })
    readonly showClearModal!: boolean;

    @Prop({ type: String, default: DEFAULT_CLEAR_MESSAGE })
    readonly clearModalMessage!: string;

    @Prop({ type: Boolean, default: false })
    readonly disableSaveAfterClick!: boolean;

    // Declarative alternative to calling resetSaveState through a ref: change this and the latch
    // releases. Any change releases — the value is a signal, not an identifier. A consumer that holds
    // a ref can keep using resetSaveState; one that binds this cannot silently lose the release to a
    // refactor that moves the buttons behind another component.
    @Prop({ type: Number, default: 0 })
    readonly saveReleaseId!: number;

    private saveWasClicked = false;

    @Watch('saveReleaseId')
    onSaveReleaseIdChanged(): void {
        this.resetSaveState();
    }

    get containerClasses(): Record<string, boolean> {
        return {
            'container py-3': !this.sticky,
            'sticky glass-pane': this.sticky
        };
    }

    get isNewDisabled(): boolean {
        if (this.disable != null) {
            return !this.disable;
        }
        return this.disableNew;
    }

    get isClearDisabled(): boolean {
        return this.disable || this.disableClear;
    }

    get isSaveDisabled(): boolean {
        // The latch is sticky once armed: a consumer whose "busy" flag only turns on asynchronously
        // would otherwise re-enable Save the instant that flag cleared, reopening the double-click window.
        if (this.saveWasClicked) {
            return true;
        }
        return this.disable || this.disableSave;
    }

    @Emit('save-clicked')
    handleSave(): void {
        // Arm before the emit so the button is already disabled when the consumer's handler runs,
        // rather than depending on that handler to flip a prop back into this component in time.
        if (this.disableSaveAfterClick) {
            this.saveWasClicked = true;
        }
    }

    public resetSaveState(): void {
        this.saveWasClicked = false;
    }

    @Emit('save-and-stay-clicked')
    handleSaveAndStay(): void {
        // Event emitted
    }

    @Emit('new-clicked')
    handleNew(): void {
        // Event emitted
    }

    @Emit('cancel-clicked')
    handleCancel(): void {
        // Event emitted
    }

    async handleClear(): Promise<void> {
        if (!this.showClearModal) {
            this.$emit('clear-clicked');
            return;
        }

        try {
            const confirmed = await this.$bvModal.msgBoxConfirm(this.clearModalMessage, {
                title: 'Confirm',
                size: 'sm',
                buttonSize: 'sm',
                okVariant: 'danger',
                okTitle: 'YES',
                cancelTitle: 'NO',
                footerClass: 'p-2',
                hideHeaderClose: false,
                centered: true
            });

            if (!confirmed) return;
            this.$emit('clear-clicked');
        } catch (error) {
            console.error('Error in clear confirmation:', error);
        }
    }
}
</script>

<style scoped lang="scss">
.sticky {
    position: sticky;
    bottom: 0;
    z-index: 100;
    border-top: 1px solid rgba(0, 0, 0, 0.05);
    box-shadow: inset 0px 7px 6px -6px rgba(0, 0, 0, 0.1);
    margin: 30px -15px -10px -15px;
    padding: 4px 0;
}
</style>
